import * as THREE from "three";
import { humanSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";

type Baller = {
  mesh: THREE.Mesh;
  shadow: THREE.Mesh;
  ready: ReturnType<typeof humanSprite>;
  shoot: ReturnType<typeof humanSprite>;
  x: number;
  z: number;
  y: number;
  tx: number;
  tz: number;
  homeX: number;
  homeZ: number;
  facing: number;
};

type Play =
  | { kind: "pass"; from: number; to: number; t: number; dur: number }
  | { kind: "shoot"; shooter: number; hoop: 0 | 1; t: number; dur: number; scored: boolean }
  | { kind: "rebound"; to: number; t: number; dur: number; fromX: number; fromZ: number; fromY: number };

/**
 * Ambient pickup game: four players cut, pass, and take shots at both hoops.
 * One shared 3D ball - sprites never hold a painted basketball.
 */
export class ParkBasketballMatch {
  readonly group = new THREE.Group();
  private readonly players: Baller[] = [];
  private readonly ball: THREE.Mesh;
  private readonly groundY: (x: number, z: number) => number;
  private readonly courtX: number;
  private readonly courtZ: number;
  private readonly hoopN: { x: number; z: number };
  private readonly hoopS: { x: number; z: number };
  private readonly rimH = 1.35;
  private play: Play;
  private holder = 0;
  private passCount = 0;
  private readonly moveSpeed = 2.4;

  constructor(
    courtX: number,
    courtZ: number,
    groundY: (x: number, z: number) => number,
    hoopNorth: { x: number; z: number },
    hoopSouth: { x: number; z: number },
    variant = 0,
  ) {
    this.courtX = courtX;
    this.courtZ = courtZ;
    this.groundY = groundY;
    this.hoopN = hoopNorth;
    this.hoopS = hoopSouth;

    // Four corners of the paint / wings - room to cut.
    const homes: [number, number][] = [
      [courtX - 2.4, courtZ - 4.2],
      [courtX + 2.6, courtZ - 3.6],
      [courtX - 2.2, courtZ + 3.8],
      [courtX + 2.8, courtZ + 4.4],
    ];

    for (let i = 0; i < 4; i++) {
      const [hx, hz] = homes[i]!;
      const v = variant + i * 7;
      const ready = humanSprite("basketReady", v);
      const shoot = humanSprite("basketShoot", v);
      const y = groundY(hx, hz);

      const shadow = makeShadow(ready.width);
      shadow.position.set(hx, y + 0.02, hz);

      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(ready.width, ready.height),
        billboardMaterial(ready.texture),
      );
      mesh.position.set(hx, y + ready.height * SPRITE_FOOT_Y, hz);
      mesh.renderOrder = 3;

      this.players.push({
        mesh,
        shadow,
        ready,
        shoot,
        x: hx,
        z: hz,
        y,
        tx: hx,
        tz: hz,
        homeX: hx,
        homeZ: hz,
        facing: i < 2 ? Math.PI / 2 : -Math.PI / 2,
      });
      this.group.add(shadow, mesh);
    }

    this.ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.14, 10, 8),
      new THREE.MeshLambertMaterial({ color: 0xe65100 }),
    );
    this.ball.castShadow = false;
    this.group.add(this.ball);

    this.holder = 0;
    this.play = {
      kind: "pass",
      from: 0,
      to: 1,
      t: 0,
      dur: 1.15,
    };
    this.assignCuts(0, 1);
  }

  update(dtSec: number, timeMs: number, camera: THREE.Camera): void {
    this.movePlayers(dtSec);
    this.advancePlay(dtSec);

    for (let i = 0; i < this.players.length; i++) {
      const p = this.players[i]!;
      const shooting =
        this.play.kind === "shoot" &&
        this.play.shooter === i &&
        this.play.t > 0.08 &&
        this.play.t < 0.55;
      const passing =
        this.play.kind === "pass" &&
        this.play.from === i &&
        this.play.t < 0.22;
      setHumanMap(p.mesh, shooting || passing ? p.shoot : p.ready);

      const bob = 1 + Math.sin(timeMs * 0.006 + p.homeX) * 0.012;
      const jog = Math.hypot(p.tx - p.x, p.tz - p.z) > 0.12 ? 0.03 * Math.sin(timeMs * 0.02 + i) : 0;
      p.mesh.position.set(p.x, p.y + p.ready.height * SPRITE_FOOT_Y * bob + jog, p.z);
      p.shadow.position.set(p.x, p.y + 0.02, p.z);

      // Face the ball / hoop / cut target.
      let faceX = this.ball.position.x;
      let faceZ = this.ball.position.z;
      if (this.play.kind === "shoot" && this.play.shooter === i) {
        const hoop = this.play.hoop === 0 ? this.hoopN : this.hoopS;
        faceX = hoop.x;
        faceZ = hoop.z;
      } else if (Math.hypot(p.tx - p.x, p.tz - p.z) > 0.2) {
        faceX = p.tx;
        faceZ = p.tz;
      }
      p.facing = Math.atan2(faceZ - p.z, faceX - p.x);
      faceCameraYaw(p.mesh, camera);
      const flip = facingFlipSign(p.x, p.z, p.facing, camera);
      p.mesh.scale.set(flip, 1, 1);
    }
  }

  destroy(): void {
    this.group.removeFromParent();
    this.ball.geometry.dispose();
    (this.ball.material as THREE.Material).dispose();
    for (const p of this.players) {
      p.mesh.geometry.dispose();
      // Contact shadows use shared geo/mat - don't dispose.
    }
    this.players.length = 0;
  }

  private movePlayers(dtSec: number): void {
    for (const p of this.players) {
      const dx = p.tx - p.x;
      const dz = p.tz - p.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.04) {
        p.x = p.tx;
        p.z = p.tz;
      } else {
        const step = Math.min(dist, this.moveSpeed * dtSec);
        p.x += (dx / dist) * step;
        p.z += (dz / dist) * step;
      }
      p.y = this.groundY(p.x, p.z);
    }
  }

  private advancePlay(dtSec: number): void {
    this.play.t += dtSec / this.play.dur;
    if (this.play.kind === "pass") {
      this.updatePassBall();
      if (this.play.t >= 1) {
        this.holder = this.play.to;
        this.passCount += 1;
        // After a few passes, take a shot; otherwise keep the ball moving.
        if (this.passCount >= 2 + (this.holder % 2)) {
          this.startShoot(this.holder);
        } else {
          this.startPass(this.holder);
        }
      }
      return;
    }
    if (this.play.kind === "shoot") {
      this.updateShootBall();
      if (this.play.t >= 1) {
        const shooter = this.play.shooter;
        const hoop = this.play.hoop === 0 ? this.hoopN : this.hoopS;
        const rimY = this.groundY(hoop.x, hoop.z) + this.rimH;
        // Rebound to someone who isn't the shooter.
        let to = (shooter + 1 + Math.floor(Math.random() * 3)) % 4;
        if (to === shooter) {
          to = (to + 1) % 4;
        }
        this.play = {
          kind: "rebound",
          to,
          t: 0,
          dur: 0.85,
          fromX: hoop.x,
          fromZ: hoop.z,
          fromY: rimY - 0.15,
        };
        this.nudgeToward(to, hoop.x, hoop.z, 2.2);
        this.scatterOthers(to, shooter);
      }
      return;
    }
    // rebound
    this.updateReboundBall();
    if (this.play.t >= 1) {
      this.holder = this.play.to;
      this.passCount = 0;
      this.startPass(this.holder);
    }
  }

  private startPass(from: number): void {
    let to = (from + 1 + Math.floor(Math.random() * 3)) % 4;
    if (to === from) {
      to = (from + 1) % 4;
    }
    this.assignCuts(from, to);
    this.play = {
      kind: "pass",
      from,
      to,
      t: 0,
      dur: 0.95 + Math.random() * 0.35,
    };
  }

  private startShoot(shooter: number): void {
    const p = this.players[shooter]!;
    // Prefer the nearer hoop, with a little mix so both get used.
    const dN = Math.hypot(p.x - this.hoopN.x, p.z - this.hoopN.z);
    const dS = Math.hypot(p.x - this.hoopS.x, p.z - this.hoopS.z);
    let hoop: 0 | 1 = dN < dS ? 0 : 1;
    if (Math.random() < 0.22) {
      hoop = hoop === 0 ? 1 : 0;
    }
    const target = hoop === 0 ? this.hoopN : this.hoopS;
    // Drive a few steps toward the basket before releasing.
    this.nudgeToward(shooter, target.x, target.z, 3.4);
    this.scatterOthers(shooter, -1);
    this.passCount = 0;
    this.play = {
      kind: "shoot",
      shooter,
      hoop,
      t: 0,
      dur: 1.45,
      scored: Math.random() < 0.72,
    };
  }

  private assignCuts(from: number, to: number): void {
    // Receiver cuts to an open spot; passer steps into space; others drift.
    for (let i = 0; i < this.players.length; i++) {
      const p = this.players[i]!;
      if (i === to) {
        p.tx = this.courtX + (Math.random() - 0.5) * 5.2;
        p.tz = this.courtZ + (Math.random() - 0.5) * 7.5;
      } else if (i === from) {
        p.tx = p.x + (Math.random() - 0.5) * 1.8;
        p.tz = p.z + (Math.random() - 0.5) * 1.8;
      } else {
        p.tx = p.homeX + (Math.random() - 0.5) * 3.2;
        p.tz = p.homeZ + (Math.random() - 0.5) * 2.6;
      }
      this.clampCourt(p);
    }
  }

  private nudgeToward(i: number, x: number, z: number, dist: number): void {
    const p = this.players[i]!;
    const dx = x - p.x;
    const dz = z - p.z;
    const len = Math.hypot(dx, dz) || 1;
    const step = Math.min(dist, len * 0.55);
    p.tx = p.x + (dx / len) * step;
    p.tz = p.z + (dz / len) * step;
    this.clampCourt(p);
  }

  private scatterOthers(keep: number, keep2: number): void {
    for (let i = 0; i < this.players.length; i++) {
      if (i === keep || i === keep2) {
        continue;
      }
      const p = this.players[i]!;
      p.tx = p.homeX + (Math.random() - 0.5) * 3.5;
      p.tz = p.homeZ + (Math.random() - 0.5) * 3.0;
      this.clampCourt(p);
    }
  }

  private clampCourt(p: Baller): void {
    p.tx = Math.max(this.courtX - 3.4, Math.min(this.courtX + 3.6, p.tx));
    p.tz = Math.max(this.courtZ - 6.2, Math.min(this.courtZ + 6.2, p.tz));
  }

  private updatePassBall(): void {
    const play = this.play;
    if (play.kind !== "pass") {
      return;
    }
    const a = this.players[play.from]!;
    const b = this.players[play.to]!;
    const u = Math.min(1, play.t);
    const ease = u * u * (3 - 2 * u);
    const x = a.x + (b.x - a.x) * ease;
    const z = a.z + (b.z - a.z) * ease;
    const base = Math.max(a.y, b.y) + 0.75;
    const arc = Math.sin(Math.PI * ease) * 1.35;
    this.ball.position.set(x, base + arc, z);
  }

  private updateShootBall(): void {
    const play = this.play;
    if (play.kind !== "shoot") {
      return;
    }
    const shooter = this.players[play.shooter]!;
    const hoop = play.hoop === 0 ? this.hoopN : this.hoopS;
    const rimY = this.groundY(hoop.x, hoop.z) + this.rimH;
    const u = Math.min(1, play.t);
    // Hold near hands briefly, then arc to rim.
    if (u < 0.18) {
      this.ball.position.set(shooter.x + 0.15, shooter.y + 1.15, shooter.z);
      return;
    }
    const t = (u - 0.18) / 0.82;
    const ease = t * t * (3 - 2 * t);
    const x = shooter.x + (hoop.x - shooter.x) * ease;
    const z = shooter.z + (hoop.z - shooter.z) * ease;
    const startY = shooter.y + 1.2;
    const endY = play.scored ? rimY + 0.05 : rimY + 0.55;
    const arc = Math.sin(Math.PI * ease) * (play.scored ? 2.1 : 2.4);
    this.ball.position.set(x, startY + (endY - startY) * ease + arc * (1 - ease * 0.35), z);
  }

  private updateReboundBall(): void {
    const play = this.play;
    if (play.kind !== "rebound") {
      return;
    }
    const catcher = this.players[play.to]!;
    const u = Math.min(1, play.t);
    const ease = u * u * (3 - 2 * u);
    const x = play.fromX + (catcher.x - play.fromX) * ease;
    const z = play.fromZ + (catcher.z - play.fromZ) * ease;
    const endY = catcher.y + 0.85;
    const dip = Math.sin(Math.PI * ease) * 0.55;
    // Drop through the net a bit, then bounce out to the catcher.
    const y = play.fromY + (endY - play.fromY) * ease - dip * (1 - ease);
    this.ball.position.set(x, y, z);
  }
}

function makeShadow(width: number): THREE.Mesh {
  return makeContactShadow({
    radius: Math.min(0.58, width * 0.4),
    squash: 1,
    opacity: 0.88,
  });
}

function setHumanMap(
  mesh: THREE.Mesh,
  sheet: ReturnType<typeof humanSprite>,
): void {
  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial && mat.map !== sheet.texture) {
    mat.map = sheet.texture;
    mat.needsUpdate = true;
  }
}
